import { useLingui } from '@lingui/react/macro';
import type { FC } from 'react';
import { useAsync } from 'react-use';
import { useParams } from 'wouter';
import { Breadcrumb } from '@/base/Breadcrumb';
import { SEO_ORIGIN } from '@/base/constants';
import { PageMeta } from '@/base/PageMeta';
import { LandingLayout } from '@/landing/LandingLayout';
import { useSettings } from '@/settings/api';
import { cn } from '@/shared/react';
import { usePupeteerSignal } from '@/shared/usePupeteerSignal';
import { Doc } from './Doc';
import { DocsSidebar } from './DocsSidebar';
import { pages } from './pages';

export const DocPage: FC = () => {
  const { page } = useParams<{ page: string }>();
  const [{ language }] = useSettings();
  const {
    i18n: { locale },
    t,
  } = useLingui();

  const content = useAsync(async () => {
    const langs = pages[page];
    // Fall back to en while the translation is not pulled yet.
    const importFn = langs?.[locale] ?? langs?.en;
    return importFn ? await importFn() : undefined;
  }, [page, locale]);

  usePupeteerSignal(!!content.value);

  return (
    <LandingLayout headerPosition="relative">
      <PageMeta
        title={content.value?.attributes?.title}
        description={content.value?.attributes?.description}
        localizedPath={`/${page}`}
      />
      <Breadcrumb
        className="w-full max-w-desktop mx-auto px-3"
        items={[
          { name: t`Home`, url: `${SEO_ORIGIN}/${language}` },
          { name: t`Guide`, url: `${SEO_ORIGIN}/${language}/guide` },
          {
            name: content.value?.attributes?.title ?? page,
            url: `${SEO_ORIGIN}/${language}/${page}`,
          },
        ]}
      />
      <main className="w-full max-w-desktop mx-auto px-3 pt-3 pb-8 flex flex-col desktop:flex-row gap-8 items-start">
        <Doc
          raw={`<h1>${content.value?.attributes?.title ?? page}</h1><hr></hr>${content.value?.html ?? ''}`}
          className={cn(
            !content.value && 'shimmer',
            'min-h-96 grow min-w-0 border rounded-2xl border-border-disabled p-6',
          )}
        />
        <DocsSidebar
          activeSlug={page}
          className="w-full desktop:w-72 shrink-0"
        />
      </main>
    </LandingLayout>
  );
};
